/* Tire family consolidation — JSON-asset driven.
   Reads gara-tire-families.json (built locally + pushed as theme asset) and:
     1. On collection grids + homepage carousel: hides duplicate cards (same
        tire family), keeps lowest-priced master, rewrites card title to
        family name + "From $X" pricing.
     2. On PDP: injects a F/R toggle + size dropdown that lets the customer
        switch to a different SKU's PDP.
   No metafield dependency — looks up by product URL.
   Search behavior unchanged — individual SKU titles still contain
   "Family - Size (Position)" so storefront search hits the exact SKU. */
(function () {
  'use strict';

  var familiesPromise = null;
  // Rider clicked "Show all tires" -> drop the bike's complete-set filter for this
  // page view and browse the whole catalog (still deduped by family).
  var showAll = false;

  function loadFamilies() {
    if (familiesPromise) return familiesPromise;
    // Resolve actual asset URL: Shopify CDNs the assets folder; the script tag
    // can tell us the theme version via its own src.
    var scripts = document.querySelectorAll('script[src*="gara-tire-card-grouper"]');
    var base = '';
    var ver = '';
    if (scripts.length) {
      var src = scripts[0].src;
      base = src.replace(/gara-tire-card-grouper\.js.*$/, '');
      // Prefer the manual &_cb= deploy token (bumped in theme.liquid on every
      // families rebuild) over the asset_url ?v= — on a PDP there is no
      // GARA_TIRE_FINDER.familiesVersion, so the families.json fetch falls back to
      // THIS, and ?v= (the theme asset hash) does not change when only the DATA
      // (families.json) is rebuilt. Keying off _cb makes a families rebuild reach
      // the PDP picker too (the alpha-size tires were invisible there otherwise).
      ver = (src.match(/[?&]_cb=([^&]+)/) || [])[1] ||
            (src.match(/[?&]v=([^&]+)/) || [])[1] || '';
    }
    // Cache-bust the families.json fetch off the families DATA version — a content
    // hash of families.json that tire_assets.build() stamps into GARA_TIRE_FINDER
    // (re-fetched fresh on every deploy). The bare /cdn/.../gara-tire-families.json
    // path is CDN-cached with no version, so a rebuilt families.json used to serve
    // STALE for hours whenever THIS grouper script was byte-identical (its own hash
    // unchanged on a data-only rebuild) — which showed wrong/incomplete families for
    // the selected bike. Keying off the data hash busts the cache on ANY families
    // rebuild. Falls back to this script's hash when the finder payload isn't present
    // (non-tire pages, where bike filtering doesn't run anyway).
    var dataVer = (window.GARA_TIRE_FINDER && window.GARA_TIRE_FINDER.familiesVersion) || '';
    var bust = dataVer || ver;
    var url = base ? base + 'gara-tire-families.json' : '/assets/gara-tire-families.json';
    if (bust) url += '?v=' + encodeURIComponent(bust);
    familiesPromise = fetch(url).then(function (r) {
      if (!r.ok) throw new Error('families.json ' + r.status);
      return r.json();
    }).catch(function (e) {
      console.warn('[gara-tire] failed to load families.json:', e);
      return {};
    });
    return familiesPromise;
  }

  // Build URL → familyName lookup once
  function buildUrlIndex(families) {
    var idx = {};
    Object.keys(families).forEach(function (fam) {
      families[fam].forEach(function (sku) {
        idx[sku.url.replace(/\?.*$/, '')] = fam;
      });
    });
    return idx;
  }


  function moneyFromCents(c) {
    if (typeof c !== 'number' || c <= 0) return '';
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(c, '${{amount}}');
    }
    return '$' + (c / 100).toFixed(2);
  }

  function normalizeUrl(href) {
    if (!href) return '';
    // Strip protocol + domain, query, and `collections/.../` prefix
    var path = href.replace(/^https?:\/\/[^/]+/, '').replace(/\?.*$/, '');
    var m = path.match(/\/products\/[^/]+/);
    return m ? m[0] : path;
  }

  // ISO speed symbols, SLOWEST -> FASTEST (mirrors tire_size.SPEED_ORDER on the
  // builder side so server + client agree on "best"). A tire's service rating
  // ('57H', '60W') is load index + speed symbol — what makes two SAME-SIZE tires
  // genuinely different products. ratingRank turns it into a sortable number so
  // the size dropdown can default to the best (fastest, then highest-load) variant.
  var SPEED_ORDER = 'BCDEFGHJKLMNPQRSTUVWY';
  function ratingRank(rating) {
    if (!rating) return -1;
    var up = String(rating).toUpperCase();
    var sm = up.match(/([B-Y])\s*$/);
    var speed = (sm && SPEED_ORDER.indexOf(sm[1]) >= 0) ? SPEED_ORDER.indexOf(sm[1]) : -1;
    var lm = up.match(/^(\d{2,3})/);
    var load = lm ? parseInt(lm[1], 10) : -1;
    return speed * 1000 + load;     // speed dominates, load breaks ties
  }
  // Display label for a dropdown option: 'size  rating' ('120/70-17  57H'),
  // plus the builder-emitted note that tells same-size twins apart
  // ('90/90-21  54H · Tube Type' vs '· Tubeless' — without it the AX41T
  // tube/tubeless pair rendered as two identical rows).
  function sizeRatingLabel(s) {
    var label = s.rating ? (s.size + '  ' + s.rating) : s.size;
    return s.note ? (label + ' · ' + s.note) : label;
  }
  // Split a rating into {speed, load}. speed is the SPEED_ORDER index (higher =
  // faster), load is the ISO load index. -1 when absent.
  function ratingParts(r) {
    if (!r) return null;
    var up = String(r).toUpperCase();
    var sm = up.match(/([B-Y])\s*$/);
    var lm = up.match(/^(\d{2,3})/);
    return {
      speed: (sm && SPEED_ORDER.indexOf(sm[1]) >= 0) ? SPEED_ORDER.indexOf(sm[1]) : -1,
      load: lm ? parseInt(lm[1], 10) : -1
    };
  }
  // Compare a tire's rating to the bike's OE rating -> 'exceeds'|'meets'|'below'|null.
  // SAFETY: load AND speed are judged INDEPENDENTLY — a tire that is faster but
  // can't carry the OE load is still BELOW (a higher speed letter must never mask
  // a short load index). Below if EITHER dimension falls short of OE.
  function compareOE(tireRating, oeRating) {
    var t = ratingParts(tireRating), o = ratingParts(oeRating);
    if (!t || !o || (o.speed < 0 && o.load < 0)) return null;
    var speedShort = o.speed >= 0 && t.speed >= 0 && t.speed < o.speed;
    var loadShort = o.load >= 0 && t.load >= 0 && t.load < o.load;
    if (speedShort || loadShort) return 'below';
    var speedOver = o.speed >= 0 && t.speed >= 0 && t.speed > o.speed;
    var loadOver = o.load >= 0 && t.load >= 0 && t.load > o.load;
    return (speedOver || loadOver) ? 'exceeds' : 'meets';
  }

  // ── COLLECTION + CAROUSEL: dedup cards by family ────────────────────────
  // `excludeFamily` (optional): hide EVERY card belonging to this family.
  //   Used on tire PDPs so "You may also like" doesn't suggest other sizes
  //   of the same tire the customer is already looking at.
  function groupCards(root, families, urlIndex, excludeFamily) {
    // Candidate cards = any <a> linking to /products/* — EXCEPT the homepage
    // curated tire sections: the "Top Track/MX Tires" carousels (.gara-tt-strip)
    // AND the "Run This Weekend / Race-Day Kit" shortlist (.gara-rtw). Those are
    // curated picks (and the .gara-tt-strip ones have their own bike filter,
    // filterCarousel, off families.json + the compact bike-size DB, present
    // site-wide). groupCards instead needs GARA_TIRE_FINDER.sizes (ymmMatchSets),
    // which is ONLY loaded on tire/finder pages, NOT the homepage. Letting
    // groupCards grab a homepage carousel made ymmMatchSets() return null ->
    // "no complete set" -> it hid the cards and injected the collection "showing
    // tires that fit one end" bar onto the homepage (the .gara-rtw section was
    // missed by the original .gara-tt-strip-only fix). Scope groupCards to real grids.
    var anchors = Array.prototype.filter.call(
      root.querySelectorAll('a[href*="/products/"]'),
      function (a) { return !(a.closest && (a.closest('.gara-tt-strip') || a.closest('.gara-rtw'))); }
    );
    if (!anchors.length) return;

    // Bucket: family → [cards in DOM order]
    var byFam = {};
    anchors.forEach(function (a) {
      var url = normalizeUrl(a.getAttribute('href'));
      var fam = urlIndex[url];
      if (!fam) return;
      a.setAttribute('data-tire-family', fam);
      (byFam[fam] = byFam[fam] || []).push(a);
    });

    // When a bike is selected, only KEEP a family if it can outfit BOTH ends of
    // the bike — i.e. it has a FRONT-position tire in the bike's front size AND a
    // REAR-position tire in the bike's rear size. A family that only covers one
    // end (e.g. the AX41 has a 17in rear but NO 17in front for a supermoto) is
    // HIDDEN — a customer must never be shown a tire they can't pair (this is the
    // #1006 mislabel class, prevented at the discovery layer). matchSets mirrors
    // the finder's GARA_TIRE_FINDER.sizes source so the two always agree.
    // `showAll` (the rider clicked "Show all tires") drops the bike filter and
    // browses the whole catalog (still deduped by family). Otherwise, when a bike
    // is selected we filter to COMPLETE SETS.
    var matchSets = showAll ? null : ymmMatchSets();

    // Classify each family for the selected bike, then decide GLOBALLY what to show:
    //   complete = covers BOTH declared ends (a matched front+rear set)
    //   partial  = covers only ONE end (front OR rear)
    //   none     = covers neither
    // If ANY family is a complete set -> show only complete sets (hide partials),
    // so a rider with real options never sees a tire they can't pair. If NONE is a
    // complete set -> fall back to showing the PARTIAL fits (tires that fit one end,
    // across families) + a "no complete set" note, so the rider isn't dead-ended.
    var famClass = {}, anyComplete = false;
    if (matchSets) {
      Object.keys(byFam).forEach(function (fam) {
        var cs = byFam[fam];
        var hasF = cs.some(function (c) { var s = skuOf(c); return s && matchSets.front[s]; });
        var hasR = cs.some(function (c) { var s = skuOf(c); return s && matchSets.rear[s]; });
        var fOK = !matchSets.haveFront || hasF, rOK = !matchSets.haveRear || hasR;
        var cls = (fOK && rOK && (hasF || hasR)) ? 'complete' : ((hasF || hasR) ? 'partial' : 'none');
        famClass[fam] = cls;
        if (cls === 'complete') anyComplete = true;
      });
    }
    var keepClass = anyComplete ? 'complete' : 'partial';

    Object.keys(byFam).forEach(function (fam) {
      var cards = byFam[fam];

      // Excluded family: hide ALL cards (and their grid-item wrappers).
      if (excludeFamily && fam === excludeFamily) {
        cards.forEach(function (c) {
          var item = c.closest('.grid__item, .grid-product, .product-card, li, .gara-pcard') || c;
          item.style.display = 'none';
          item.setAttribute('data-tire-hidden-same-family', '1');
        });
        return;
      }

      // Pick the master + decide which cards to hide. (Reversible: originals are
      // stashed below so ungroupCollection() can restore on bike change.)
      //
      // AVAILABILITY-AWARE PICK (Benjamin 2026-07-16): each size is its own
      // one-variant product, so the old cards[0] pick let a sold-out size become
      // the face of the family — the card wore ITS "Sold Out" badge while other
      // sizes sat in stock (Conti Attack SM 2: 3 of 5 sizes stocked, card said
      // SOLD OUT). Prefer an in-stock card as master; only when EVERY size is
      // out does the family card honestly read Sold Out. No badge mutation —
      // choosing an available master keeps grouping fully reversible.
      function inStock(c) {
        if (c.classList && c.classList.contains('oos')) return false;          // gara-pcard
        if (c.querySelector && c.querySelector('.grid-product__tag--sold-out')) return false; // grid-product
        var item = c.closest && c.closest('.grid__item, .grid-product');
        if (item && item.querySelector && item.querySelector('.grid-product__tag--sold-out')) return false;
        return true;
      }
      function firstInStock(list) {
        for (var i = 0; i < list.length; i++) { if (inStock(list[i])) return list[i]; }
        return null;
      }
      var master, partialLabel = '';
      if (matchSets) {
        if (famClass[fam] !== keepClass) {
          // Wrong class for this bike (e.g. a partial when complete sets exist, or
          // a no-fit family) — hide every card.
          cards.forEach(function (c) { c.style.display = 'none'; c.setAttribute('data-tire-hidden', '1'); });
          return;
        }
        var frontCards = cards.filter(function (c) { var s = skuOf(c); return s && matchSets.front[s]; });
        var rearCards  = cards.filter(function (c) { var s = skuOf(c); return s && matchSets.rear[s]; });
        // Prefer a front-fitting card as the master so the kept card is relevant;
        // within each end, prefer one that's in stock.
        master = firstInStock(frontCards) || firstInStock(rearCards) ||
                 frontCards[0] || rearCards[0] || firstInStock(cards) || cards[0];
        // When we're showing partials (no complete set exists), label which end fits.
        if (keepClass === 'partial') partialLabel = frontCards.length ? 'Fits your front' : 'Fits your rear';
      } else {
        // A lone card on the page still represents its WHOLE family — rewrite it to
        // the family name + price RANGE (from families.json) so a multi-size family
        // never shows one size's native "From $X" (e.g. Dunlop D604: front on this
        // page, rear elsewhere). Only skip TRULY single-member families (one product).
        if (cards.length < 2 && (families[fam] || []).length < 2) return;
        // Prefer a card the tire-finder layer isn't hiding (.gtf-hidden): a master
        // picked from a finder-hidden card renders the whole family invisible in
        // the Show-all view (single-card grid, 2026-08-14).
        var visCards = cards.filter(function (c) { return !(c.classList && c.classList.contains('gtf-hidden')); });
        master = firstInStock(visCards) || visCards[0] || firstInStock(cards) || cards[0];
      }
      cards.forEach(function (c) {
        if (c === master) return;
        c.style.display = 'none';
        c.setAttribute('data-tire-hidden', '1');
      });

      // Rewrite title text on the master
      var skus = families[fam];
      // Family price RANGE: lowest -> highest across the family's sizes (was a
      // single 'From $min'; Benjamin wants the full range shown).
      var fprices = skus.map(function (s) { return s.price; }).filter(function (p) { return p > 0; });
      var minPrice = fprices.length ? Math.min.apply(null, fprices) : 0;
      var maxPrice = fprices.length ? Math.max.apply(null, fprices) : 0;

      // Look for the title element under the master
      var titleEl = master.querySelector('.gara-pcard-title, .gara-tt-name, .product-card__title, h3');
      if (titleEl) {
        if (titleEl.getAttribute('data-g-orig') == null) titleEl.setAttribute('data-g-orig', titleEl.textContent);
        titleEl.textContent = fam;
        titleEl.style.fontWeight = '600';
      }
      // Partial-fit label AFTER the title rewrite (textContent=fam would wipe it).
      // Reversible: ungroupCollection removes .gara-tire-fitlabel.
      if (partialLabel && titleEl && !titleEl.querySelector('.gara-tire-fitlabel')) {
        var lbl = document.createElement('span');
        lbl.className = 'gara-tire-fitlabel';
        lbl.textContent = partialLabel;
        titleEl.appendChild(lbl);
      }

      // Render the family price RANGE on the master card ('$min – $max', or a
      // single '$min' when every size is the same price / there is one size).
      var priceEl = master.querySelector('.gara-pcard-price, .gara-tt-price, .product-card__price, .price');
      if (priceEl && minPrice && !priceEl.dataset.tireFromAdded) {
        if (priceEl.getAttribute('data-g-orig-html') == null) priceEl.setAttribute('data-g-orig-html', priceEl.innerHTML);
        var compareEl = priceEl.querySelector('.gara-tt-compare, .price__compare, s, del');
        priceEl.innerHTML = (maxPrice > minPrice)
          ? moneyFromCents(minPrice) + '<span class="gara-tire-range"> – </span>' + moneyFromCents(maxPrice)
          : moneyFromCents(minPrice);
        if (compareEl) priceEl.appendChild(compareEl);
        priceEl.dataset.tireFromAdded = '1';
      }
    });
    return { bikeActive: ymmActive() && !!ymmSizes(), anyComplete: anyComplete, showAll: showAll };
  }

  // True when the rider has a bike selected (GaraYMM is the single source).
  function ymmActive() {
    try { return !!(window.GaraYMM && window.GaraYMM.get && window.GaraYMM.get()); } catch (_) { return false; }
  }

  // The SKU on a product card anchor (or its nearest [data-sku]).
  function skuOf(anchor) {
    if (!anchor) return null;
    if (anchor.getAttribute && anchor.getAttribute('data-sku')) return anchor.getAttribute('data-sku');
    var el = (anchor.querySelector && anchor.querySelector('[data-sku]')) ||
             (anchor.closest && anchor.closest('[data-sku]'));
    return el ? el.getAttribute('data-sku') : null;
  }

  // The selected bike's stock {front, rear, front_alt, rear_alt} sizes, or null.
  function ymmSizes() {
    try { return (window.GaraYMM && window.GaraYMM.getTireSizes && window.GaraYMM.getTireSizes()) || null; }
    catch (_) { return null; }
  }

  // ── "We don't know your bike's sizes" note ───────────────────────────────
  // The one YMM state the grouper can't help with: a bike IS selected but the
  // spine has no tire sizes for it (yet — research fills these over time). Say
  // so honestly and point the rider at the sidewall instead of silently
  // showing an unfiltered grid / a default size.
  function sizesUnknown() { return ymmActive() && !ymmSizes(); }

  function bikeLabel() {
    try {
      var b = window.GaraYMM && window.GaraYMM.get && window.GaraYMM.get();
      return b ? [b.year, b.make, b.model].filter(Boolean).join(' ') : '';
    } catch (_) { return ''; }
  }

  function escHtml(s) {
    var d = document.createElement('div');
    d.textContent = s || '';
    return d.innerHTML;
  }

  function injectUnknoteStyles() {
    if (document.getElementById('gara-tire-unknote-styles')) return;
    var style = document.createElement('style');
    style.id = 'gara-tire-unknote-styles';
    style.textContent =
      '.gara-tire-unknote{background:#0a0a0d;border:1px solid #2a2a2e;border-left:3px solid #c9a84c;' +
      'color:#c8c8cc;font-family:"Oswald",sans-serif;font-size:13px;letter-spacing:.4px;line-height:1.55;' +
      'padding:12px 16px;margin:0 0 18px;}' +
      '.gara-tire-unknote b{color:#fff;font-weight:600;}' +
      '.gara-tire-showall{margin-left:10px;background:#c9a84c;color:#0a0a0d;border:none;padding:6px 14px;' +
      'font-family:"Oswald",sans-serif;font-weight:700;font-size:11px;letter-spacing:1.6px;text-transform:uppercase;' +
      'cursor:pointer;vertical-align:middle;}' +
      '.gara-tire-showall:hover{background:#e8c870;}' +
      '.gara-tire-fitlabel{display:inline-block;margin-left:8px;font-size:10px;letter-spacing:1.2px;' +
      'text-transform:uppercase;color:#c9a84c;font-weight:600;}';
    document.head.appendChild(style);
  }

  function buildUnknote(kind) {
    injectUnknoteStyles();
    var div = document.createElement('div');
    div.className = 'gara-tire-unknote';
    div.setAttribute('data-gara-unknote', kind);
    var bike = escHtml(bikeLabel());
    div.innerHTML = kind === 'grid'
      ? "We don't have factory tire sizes on file for your <b>" + bike + "</b> yet, so we're showing " +
        "every tire family. Your sizes are printed on the tire sidewalls (something like " +
        "<b>120/70ZR17</b>). Pick a family, then choose your front and rear sizes."
      : "We don't have factory tire sizes on file for your <b>" + bike + "</b> yet. The size is " +
        "printed on your tire's sidewall (something like <b>120/70ZR17</b>). Match your front and " +
        "rear below and you're set.";
    return div;
  }

  // Collection grid: keep the banner in sync with the current bike. Sits just
  // above the grid that holds the family cards; removed the moment the bike has
  // known sizes or is cleared.
  function syncGridUnknote(root) {
    var existing = document.querySelector('[data-gara-unknote="grid"]');
    var firstCard = (root || document).querySelector('a[data-tire-family]');
    if (!sizesUnknown() || !firstCard) { if (existing) existing.remove(); return; }
    var grid = firstCard.closest('ul, ol, .grid, .collection-grid, [data-collection-grid]') || firstCard.parentElement;
    var note = buildUnknote('grid');
    if (existing) existing.replaceWith(note);
    else if (grid && grid.parentNode) grid.parentNode.insertBefore(note, grid);
  }

  // Normalize a tire size to its core width/aspect/rim so "160/60 ZR17",
  // "160/60-17", "160/60R17" and "160/60ZR17" all compare equal (the
  // speed/load/construction codes between aspect and rim are ignored). Used to
  // match a bike's stock size against a family's size-label strings, which can
  // carry the speed rating the bike record omits.
  function sizeCore(s) {
    var m = String(s || '').match(/(\d{2,3})\s*\/\s*(\d{2,3})\D*?(\d{2})\b/);
    return m ? (m[1] + '/' + m[2] + '/' + m[3]) : '';
  }

  // GARA_TIRE_FINDER.sizes is keyed by RAW size labels — "120/70-17",
  // "120/70 ZR17" and "120/70ZR17" are three separate keys. A bike whose spine
  // size reads "120/70-17" must match ALL of them, so lookups go through this
  // core→entries index instead of an exact idx[size] hit (the exact hit missed
  // every ZR-labelled tire and broke the both-ends family rule, 2026-06-11).
  var _coreIdx = null, _coreIdxSrc = null;
  function entriesForSize(idx, sz) {
    if (!idx || !sz) return [];
    if (_coreIdx === null || _coreIdxSrc !== idx) {
      _coreIdxSrc = idx;
      _coreIdx = {};
      Object.keys(idx).forEach(function (k) {
        var c = sizeCore(k);
        if (!c) return;
        (_coreIdx[c] = _coreIdx[c] || []).push(k);
      });
    }
    var core = sizeCore(sz);
    var keys = core ? (_coreIdx[core] || []) : [];
    // Fall back to the exact label when the size string has no parsable core.
    if (!keys.length && idx[sz]) keys = [sz];
    var out = [];
    keys.forEach(function (k) { (idx[k] || []).forEach(function (e) { out.push(e); }); });
    return out;
  }

  // {sku:1} of every tire that fits the selected bike, built from the SAME
  // GARA_TIRE_FINDER.sizes index the finder matches against — so the grouper's
  // "which family fits" and the finder's "which card to show" can never disagree.
  // Returns null when no bike is selected (→ normal browse-mode collapse).
  function ymmMatchSet() {
    try {
      var y = window.GaraYMM && window.GaraYMM.getTireSizes && window.GaraYMM.getTireSizes();
      if (!y) return null;
      var idx = window.GARA_TIRE_FINDER && window.GARA_TIRE_FINDER.sizes;
      if (!idx) return null;
      var set = {}, any = false;
      [y.front, y.rear, y.front_alt, y.rear_alt].forEach(function (sz) {
        entriesForSize(idx, sz).forEach(function (e) { if (e && e.sku) { set[e.sku] = 1; any = true; } });
      });
      return any ? set : null;
    } catch (_) { return null; }
  }

  // Position-aware fit sets: {front:{sku:1}, rear:{sku:1}, haveFront, haveRear}.
  //   front = tires in the bike's FRONT size(s) whose position is Front (or a
  //           universal/both tire); rear = tires in the bike's REAR size(s) whose
  //           position is Rear (or universal). haveFront/haveRear say whether the
  //   bike declares that end's size at all. Used to require a family cover BOTH
  //   ends before it is shown. Returns null when no bike is selected.
  function ymmMatchSets() {
    try {
      var y = window.GaraYMM && window.GaraYMM.getTireSizes && window.GaraYMM.getTireSizes();
      if (!y) return null;
      var idx = window.GARA_TIRE_FINDER && window.GARA_TIRE_FINDER.sizes;
      if (!idx) return null;
      var front = {}, rear = {};
      function p(e) { return ((e && e.position) || '').toLowerCase(); }
      function fitsFront(pos) { return pos === 'front' || pos === '' || pos === 'front/rear' || pos === 'universal'; }
      function fitsRear(pos)  { return pos === 'rear'  || pos === '' || pos === 'front/rear' || pos === 'universal'; }
      // haveFront/haveRear = does the BIKE DECLARE that end's size — NOT whether we
      // happen to stock it. THE BUG (fixed 2026-06-21): these were set from catalog
      // stock (`if (es.length)`), so a bike whose rear size we don't carry got
      // haveRear=false, which made the both-ends rule in groupCards drop the rear
      // requirement and SHOW front-only families that can never be a complete set.
      // Anchoring on the declared size keeps the requirement, so an unstocked end
      // means the family is correctly hidden (no complete set for this bike).
      var haveFront = !!(y.front || y.front_alt);
      var haveRear  = !!(y.rear  || y.rear_alt);
      [y.front, y.front_alt].forEach(function (sz) {
        entriesForSize(idx, sz).forEach(function (e) { if (e && e.sku && fitsFront(p(e))) { front[e.sku] = 1; } });
      });
      [y.rear, y.rear_alt].forEach(function (sz) {
        entriesForSize(idx, sz).forEach(function (e) { if (e && e.sku && fitsRear(p(e))) { rear[e.sku] = 1; } });
      });
      // Return a match-set whenever the bike DECLARES a size, even if front/rear are
      // empty (we stock neither end). That keeps groupCards on the both-ends path so
      // it HIDES every family (no complete set) instead of falling through to its
      // no-bike "show all" branch — which was showing every family for ~715 bikes
      // whose sizes we don't carry at all.
      return (haveFront || haveRear) ? { front: front, rear: rear, haveFront: haveFront, haveRear: haveRear } : null;
    } catch (_) { return null; }
  }

  // Undo collection family-collapse (restore hidden cards + original titles/
  // prices) so the next groupCards() pass can re-collapse with the current
  // bike's match set. Called on every gara-ymm:change.
  function ungroupCollection(root) {
    root = root || document;
    root.querySelectorAll('.gara-tire-fitlabel').forEach(function (l) { l.remove(); });
    root.querySelectorAll('[data-tire-hidden]').forEach(function (c) {
      c.style.display = ''; c.removeAttribute('data-tire-hidden');
    });
    root.querySelectorAll('[data-g-orig]').forEach(function (el) {
      el.textContent = el.getAttribute('data-g-orig'); el.removeAttribute('data-g-orig'); el.style.fontWeight = '';
    });
    root.querySelectorAll('[data-g-orig-html]').forEach(function (el) {
      el.innerHTML = el.getAttribute('data-g-orig-html'); el.removeAttribute('data-g-orig-html'); delete el.dataset.tireFromAdded;
    });
  }

  // ── PDP: inject the picker's own CSS (collection-card-styles snippet doesn't
  //         load on product pages, so we ship styles WITH the picker). ───────
  function injectPdpStyles() {
    if (document.getElementById('gara-tire-pdp-styles')) return;
    var css = [
      '.gtp-picker{background:#0a0a0d;border:1px solid #2a2a2e;border-left:3px solid #c9a84c;padding:16px 18px;margin:0 0 22px;display:flex;flex-direction:column;gap:14px;font-family:"Oswald",sans-serif;}',
      '.gtp-picker .gtp-head{display:flex;flex-direction:column;gap:3px;}',
      '.gtp-picker .gtp-fam{font-weight:600;font-size:17px;color:#fff;letter-spacing:.5px;line-height:1.15;}',
      '.gtp-picker .gtp-prompt{font-size:10.5px;letter-spacing:2.4px;text-transform:uppercase;color:#c9a84c;font-weight:500;}',
      '.gtp-picker .gtp-row{display:flex;flex-direction:column;gap:7px;}',
      '.gtp-picker .gtp-label{font-size:9.5px;letter-spacing:2.6px;text-transform:uppercase;color:#9a9aa0;font-weight:600;}',
      '.gtp-picker .gtp-pos{display:inline-flex;gap:0;align-self:flex-start;border:1px solid #2a2a2e;background:#000;padding:3px;}',
      '.gtp-picker .gtp-pos-btn{background:transparent;border:none;padding:10px 22px;font-family:"Oswald",sans-serif;font-weight:600;font-size:12px;letter-spacing:2.2px;text-transform:uppercase;color:#c8c8cc;cursor:pointer;transition:color .12s,background .12s;min-width:88px;}',
      '.gtp-picker .gtp-pos-btn:hover{color:#fff;background:#15151a;}',
      '.gtp-picker .gtp-pos-btn.is-active{background:#c9a84c;color:#0a0a0d;}',
      '.gtp-picker .gtp-select-wrap{position:relative;max-width:340px;width:100%;}',
      '.gtp-picker .gtp-size{background:#000;color:#fff;border:1px solid #3a3a40;padding:12px 38px 12px 14px;font-family:"Oswald",sans-serif;font-weight:500;font-size:14px;letter-spacing:.6px;appearance:none;-webkit-appearance:none;-moz-appearance:none;cursor:pointer;width:100%;border-radius:0;transition:border-color .12s,background .12s;}',
      '.gtp-picker .gtp-size:hover{border-color:#c9a84c;background:#0c0c10;}',
      '.gtp-picker .gtp-size:focus{outline:none;border-color:#c9a84c;background:#0c0c10;}',
      '.gtp-picker .gtp-chev{position:absolute;right:12px;top:50%;transform:translateY(-50%);pointer-events:none;color:#c9a84c;font-size:14px;line-height:1;}',
      '.gtp-picker .gtp-size-static{font-family:"Oswald",sans-serif;font-weight:600;font-size:15px;letter-spacing:.6px;color:#fff;padding:10px 0 0;}',
      '.gtp-picker .gtp-go{background:#c9a84c;color:#0a0a0d;border:none;padding:12px 26px;font-family:"Oswald",sans-serif;font-weight:700;font-size:12px;letter-spacing:2.4px;text-transform:uppercase;cursor:pointer;width:fit-content;transition:background .12s,transform .08s;display:inline-flex;align-items:center;gap:8px;}',
      '.gtp-picker .gtp-go:hover{background:#e8c870;}',
      '.gtp-picker .gtp-go:active{transform:translateY(1px);}',
      '.gtp-picker .gtp-go .gtp-arrow{font-size:13px;line-height:1;}',
      // ── OEM-rating badge (reflects the selected tire vs the bike's OE rating) ──
      '.gtp-oem-badge{display:inline-flex;align-items:center;gap:8px;align-self:flex-start;font-family:"Oswald",sans-serif;text-transform:uppercase;font-size:11px;font-weight:600;letter-spacing:1.7px;padding:9px 14px;border-radius:3px;line-height:1;}',
      '.gtp-oem-badge .gtp-oem-ico{font-size:12px;line-height:1;}',
      '.gtp-oem-badge .gtp-oem-sub{font-size:9px;letter-spacing:1.4px;opacity:.7;font-weight:500;padding-left:9px;margin-left:1px;border-left:1px solid currentColor;}',
      // Meets: solid Gara green.
      '.gtp-oem-meets{background:#5e6350;color:#fff;border:1px solid #5e6350;}',
      // Exceeds: Gara green with a GOLD outline + subtle gold glow (premium).
      '.gtp-oem-exceeds{background:#5e6350;color:#fff;border:1.5px solid #c9a84c;box-shadow:0 0 0 1px rgba(201,168,76,.30),0 3px 14px rgba(201,168,76,.16);}',
      '.gtp-oem-exceeds .gtp-oem-ico{color:#f0d48a;}',
      // Below: warning — rose outline, never sold as ok.
      '.gtp-oem-below{background:rgba(215,74,93,.12);color:#f0a6af;border:1px solid #d74a5d;}',
      // ── Out-of-stock badge (selected size's variant is unavailable) ──
      '.gtp-oos-badge{display:inline-flex;align-items:center;gap:7px;align-self:flex-start;font-family:"Oswald",sans-serif;text-transform:uppercase;font-size:11px;font-weight:600;letter-spacing:1.7px;padding:9px 14px;border-radius:3px;line-height:1;background:rgba(215,74,93,.12);color:#f0a6af;border:1px solid #d74a5d;}',
      '.gtp-oos-badge .gtp-oos-ico{font-size:12px;line-height:1;}',
      '.gtp-go.is-oos{background:#2a2a2e;color:#8a8a90;}',
      '.gtp-go.is-oos:hover{background:#2a2a2e;}',
      '@media (max-width:640px){.gtp-picker{padding:14px;}.gtp-picker .gtp-select-wrap{max-width:none;}.gtp-picker .gtp-pos-btn{flex:1;min-width:0;padding:10px 12px;}.gtp-picker .gtp-pos{align-self:stretch;display:flex;}.gtp-picker .gtp-go{width:100%;justify-content:center;}}'
    ].join('');
    var style = document.createElement('style');
    style.id = 'gara-tire-pdp-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  // ── PDP: render F/R + size picker ────────────────────────────────────────
  function renderPdpPicker(families, urlIndex) {
    if (!/\/products\//.test(window.location.pathname)) return;
    var url = window.location.pathname;
    var fam = urlIndex[url] || urlIndex[normalizeUrl(url)];
    if (!fam) return;
    var skus = families[fam];
    if (!skus || skus.length === 0) return;
    // Render even for 1-SKU families so every tire page has the same
    // overview UI (family name, hidden SKU bits, single Add to Cart).
    // The position toggle + size dropdown auto-hide when there's only
    // one option of each.

    var anchors = [
      '.product-single__title',
      '.product__title',
      '.product-meta__title',
      'h1[itemprop="name"]',
      'h1.product-single__title',
      '.product__info-wrapper h1',
    ];
    var target = null;
    for (var i = 0; i < anchors.length; i++) {
      target = document.querySelector(anchors[i]);
      if (target) break;
    }
    if (!target) return;

    if (document.getElementById('gara-tire-pdp-picker')) return;

    injectPdpStyles();

    // Sync picker to the actual page: match the page URL against family
    // SKUs using the SAME normalizer used to build the URL index. Without
    // normalization, trailing slashes / case / collection prefixes silently
    // fail to match and the picker falls back to skus[0] (showing front +
    // smallest front size on a rear-tire PDP).
    var pageUrl = normalizeUrl(window.location.pathname);
    var current = skus.find(function (s) { return normalizeUrl(s.url) === pageUrl; }) || skus[0];

    // The PDP underneath is a specific size's product, so its H1 reads
    // "Family — 200/60 ZR 17 (Rear)" and its SKU element shows the
    // variant SKU. On a family page, the SIZE belongs in the picker —
    // strip it from the visible title so the page reads as a family
    // overview, and hide the variant SKU readout (it changes per size).
    // The picker already prints the family name prominently at its top
    // (.gtp-fam). Hiding the giant page H1 avoids the duplicate "PIRELLI
    // DIABLO ROSSO IV CORSA" billboard below the picker. We still set its
    // textContent (in case someone CSS-overrides display:none later) and
    // mark it so we can re-show in a revert.
    target.textContent = fam;
    target.setAttribute('data-tire-family-h1', '1');
    target.style.display = 'none';
    // .gara-part-ids = this theme's "Part # / Item #" block. It is static to the
    // PAGE product, so on a family PDP it shows ONE size's part number while the
    // picker swaps the actual product underneath (looked like one part with two
    // prices — front 45006380 vs rear 45006807 both reading the rear's number).
    // Each size/position IS its own product+SKU; the picker is the source of truth.
    document.querySelectorAll('.product-single__sku, .product-meta__sku, [data-sku], .gara-part-ids').forEach(function (el) {
      if (el.closest('#gara-tire-pdp-picker')) return; // never hide things inside the picker
      el.style.display = 'none';
    });

    // The picker owns the Add to Cart action now. Kill the theme's native
    // submit button, Shop Pay / dynamic checkout button, and quantity
    // selector so we don't have two cart paths on the same page.
    document.querySelectorAll([
      '.product-single__form',
      'form[action*="/cart/add"]',
      '.payment-buttons',
      '.shopify-payment-button',
      '.shopify-payment-terms',
      '[data-add-to-cart]',
      'button[name="add"]',
      '.product-form__buttons',
      '.product-form__quantity'
    ].join(',')).forEach(function (el) {
      if (el.closest('#gara-tire-pdp-picker')) return;
      el.style.display = 'none';
    });

    // Price sync: when the user picks a different size, fetch that
    // product's price and rewrite every price element on the page. Each
    // size = its own product with its own price, so this is the only
    // way the page-level $X.XX stays accurate.
    function updatePagePrice(forUrl) {
      var h = (forUrl.match(/\/products\/([^/?#]+)/) || [])[1];
      if (!h) return;
      fetch('/products/' + h + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (prod) {
          if (!prod || !prod.variants || !prod.variants[0]) return;
          var v = prod.variants[0];
          setAvailability(v.available !== false);
          var priceStr = moneyFromCents(v.price);
          document.querySelectorAll('[data-product-price]').forEach(function (el) {
            if (el.closest('#gara-tire-pdp-picker')) return;
            el.textContent = priceStr;
          });
          if (v.compare_at_price && v.compare_at_price > v.price) {
            var cmpStr = moneyFromCents(v.compare_at_price);
            document.querySelectorAll('[data-compare-price]').forEach(function (el) {
              if (el.closest('#gara-tire-pdp-picker')) return;
              el.textContent = cmpStr;
              el.style.display = '';
            });
          } else {
            document.querySelectorAll('[data-compare-price]').forEach(function (el) {
              el.style.display = 'none';
            });
          }
        });
    }

    // Keep the spec chart in sync with the SELECTED size (Benjamin 2026-07-16):
    // the server rendered the LANDING SKU's chart, but every size is its own
    // product with its own part number AND its own per-size rows (load/speed,
    // max load, rim width, pressure). Both ship per SKU in families.json (mpn +
    // chart, built from the sanitized gara.specs store). Absent data (older
    // cached asset, honest-blank size) leaves rows hidden — never invented.
    // The pressure row's NAME varies by the maker's wording, so a size switch
    // may hide 'Max Pressure' and show 'Recommended Pressure'.
    var PER_SIZE_SPEC_ROWS = ['Load & Speed Rating', 'Max Load', 'Rim Width Range', 'Max Pressure', 'Recommended Pressure'];
    function updateSpecRows(forUrl) {
      var u = normalizeUrl(forUrl);
      var entry = null;
      for (var i = 0; i < skus.length; i++) {
        if (normalizeUrl(skus[i].url) === u) { entry = skus[i]; break; }
      }
      if (!entry) return;
      var tbody = document.querySelector('.gara-spectable__t tbody') || document.querySelector('.gara-spectable__t');
      if (!tbody) return;
      function rowOf(name) {
        var ths = tbody.querySelectorAll('th');
        for (var j = 0; j < ths.length; j++) {
          if (ths[j].textContent.replace(/\s+/g, ' ').trim() === name) return ths[j].parentElement;
        }
        return null;
      }
      if (entry.mpn) {
        var pnr = rowOf('Part Number');
        if (pnr && pnr.lastElementChild) pnr.lastElementChild.textContent = entry.mpn;
      }
      if (!entry.chart && !entry.mpn) return;
      var chart = entry.chart || {};
      // Insert-anchor: the last family-level row that precedes the per-size block
      // in the uniform schema, so created rows land in order.
      var anchor = null;
      var pre = ['Tube or Tubeless', 'Tube Type', 'Compound', 'Tread Pattern', 'Construction', 'Use Case', 'Part Number'];
      for (var k = 0; k < pre.length && !anchor; k++) anchor = rowOf(pre[k]);
      PER_SIZE_SPEC_ROWS.forEach(function (name) {
        var tr = rowOf(name);
        var val = chart[name];
        if (val) {
          if (!tr) {
            tr = document.createElement('tr');
            tr.appendChild(document.createElement('th'));
            tr.appendChild(document.createElement('td'));
            tr.firstElementChild.textContent = name;
            if (anchor && anchor.parentElement) anchor.insertAdjacentElement('afterend', tr);
            else tbody.appendChild(tr);
          }
          tr.style.display = '';
          tr.lastElementChild.textContent = val;
          anchor = tr;
        } else if (tr) {
          // The maker publishes nothing for the selected size: hide, never invent.
          // (Only when we HAVE this SKU's chart — a missing chart means a stale
          // families asset, where hiding would destroy good server rows.)
          if (entry.chart) tr.style.display = 'none';
          anchor = tr;
        }
      });
    }

    var box = document.createElement('div');
    box.id = 'gara-tire-pdp-picker';
    box.className = 'gtp-picker';

    // Header: family name + prompt
    var head = document.createElement('div');
    head.className = 'gtp-head';
    var famLabel = document.createElement('div');
    famLabel.className = 'gtp-fam';
    famLabel.textContent = fam;
    var prompt = document.createElement('div');
    prompt.className = 'gtp-prompt';
    prompt.textContent = 'Pick your fitment';
    head.appendChild(famLabel);
    head.appendChild(prompt);
    box.appendChild(head);


    // Bike selected but no sizes on file: be upfront and point at the sidewall
    // (kept in sync when the rider switches bikes without a reload).
    function syncPdpUnknote() {
      var old = box.querySelector('[data-gara-unknote="pdp"]');
      if (sizesUnknown()) {
        var note = buildUnknote('pdp');
        if (old) old.replaceWith(note);
        else head.insertAdjacentElement('afterend', note);
      } else if (old) {
        old.remove();
      }
    }
    syncPdpUnknote();
    document.addEventListener('gara-ymm:change', syncPdpUnknote);

    var positions = [];
    skus.forEach(function (s) { if (s.position && positions.indexOf(s.position) === -1) positions.push(s.position); });

    // POSITION row with explicit label
    var posRow = document.createElement('div');
    posRow.className = 'gtp-row';
    var posLabel = document.createElement('div');
    posLabel.className = 'gtp-label';
    posLabel.textContent = positions.length >= 2 ? 'Position — Front or Rear' : 'Position';
    var posDiv = document.createElement('div');
    posDiv.className = 'gtp-pos';

    // SIZE row with explicit label + chevron-wrapped select
    var sizeRow = document.createElement('div');
    sizeRow.className = 'gtp-row';
    var sizeLabel = document.createElement('div');
    sizeLabel.className = 'gtp-label';
    sizeLabel.textContent = 'Size';
    var selectWrap = document.createElement('div');
    selectWrap.className = 'gtp-select-wrap';
    var sizeSelect = document.createElement('select');
    sizeSelect.className = 'gtp-size';
    sizeSelect.setAttribute('aria-label', 'Choose tire size');
    var chev = document.createElement('span');
    chev.className = 'gtp-chev';
    chev.setAttribute('aria-hidden', 'true');
    chev.textContent = '▾';
    selectWrap.appendChild(sizeSelect);
    selectWrap.appendChild(chev);
    // OEM-rating badge: a Gara-brand badge reflecting the SELECTED tire vs the
    // bike's OE load/speed rating — meets (green), exceeds (green + gold outline),
    // or below (warning). Updates on every size/position change. Hidden until a
    // bike is known.
    var oeBadge = document.createElement('div');
    oeBadge.className = 'gtp-oem-badge';
    oeBadge.style.display = 'none';
    var curOeRating = '';   // the active position's OE rating, set by updateSizes

    // Out-of-stock badge: shown when the SELECTED size's variant is unavailable,
    // so a rider sees a sold-out size BEFORE clicking Add (not only on the attempt).
    // Driven by updatePagePrice(), which already fetches the variant on every
    // size/position change. goBtn/goLabel/setLabel are created further down but
    // assigned before this ever runs (first call is via updateSizes() at the end).
    var oosBadge = document.createElement('div');
    oosBadge.className = 'gtp-oos-badge';
    oosBadge.style.display = 'none';
    oosBadge.innerHTML = '<span class="gtp-oos-ico" aria-hidden="true">✕</span><span>Out of stock</span>';
    function setAvailability(avail) {
      if (avail === false) {
        oosBadge.style.display = 'inline-flex';
        setLabel('Out of stock', true);
        goBtn.style.cursor = 'not-allowed';
        goBtn.classList.add('is-oos');
      } else {
        oosBadge.style.display = 'none';
        goBtn.classList.remove('is-oos');
        // Only un-stick the label if WE set it to the OOS state.
        if (goBtn.disabled && /out of stock/i.test(goLabel.textContent)) setLabel('Add to cart', false);
      }
    }

    function renderOeBadge() {
      var opt = sizeSelect.options[sizeSelect.selectedIndex];
      var tireRating = opt ? (opt.getAttribute('data-rating') || '') : '';
      var verdict = curOeRating ? compareOE(tireRating, curOeRating) : null;
      if (!verdict) { oeBadge.style.display = 'none'; return; }
      var cfg = {
        exceeds: ['gtp-oem-exceeds', '★', 'Exceeds OEM rating'],
        meets:   ['gtp-oem-meets',   '✓', 'Meets OEM rating'],
        below:   ['gtp-oem-below',   '!', 'Below OEM rating']
      }[verdict];
      oeBadge.className = 'gtp-oem-badge ' + cfg[0];
      oeBadge.innerHTML = '<span class="gtp-oem-ico" aria-hidden="true">' + cfg[1] +
        '</span><span>' + cfg[2] + '</span>' +
        '<span class="gtp-oem-sub">OEM ' + escHtml(curOeRating) + '</span>';
      oeBadge.style.display = '';
    }

    function updateSizes(activePos) {
      sizeSelect.innerHTML = '';
      // When a bike is selected, the size dropdown for the toggled position must
      // default to THAT bike's stock size — not the first listed size. Landing on
      // a family from a bike-filtered grid put us on the bike's FRONT SKU (so the
      // Front toggle matched pageUrl), but toggling to Rear matched nothing and
      // silently fell to the first rear size, which may not be the bike's rear
      // (e.g. a family with 160/60-17 AND 180/55-17 rears on a bike that needs
      // 160/60-17). Pre-select by the bike's size for the active position so the
      // rear auto-sets correctly too. (#1006 pairing — never show a wrong size.)
      var sizes = ymmSizes();
      var wantCore = '';
      if (sizes) {
        if (/front/i.test(activePos)) wantCore = sizeCore(sizes.front) || sizeCore(sizes.front_alt);
        else if (/rear/i.test(activePos)) wantCore = sizeCore(sizes.rear) || sizeCore(sizes.rear_alt);
      }
      // Options carry the service rating ('120/70-17  57H') so same-size variants
      // are distinguishable (they are different tires: load + speed). Default
      // selection, in priority order:
      //   1. bike selected -> the BEST-rated (fastest, then highest-load) option
      //      whose size is the bike's stock size for this position.
      //   2. no bike -> the page's own SKU (you landed on this exact product).
      //   3. fallback -> the best-rated option overall for this position.
      // The bike's OE-required service rating for THIS position (manufacturer
      // minimum). Used to flag tires that fall below it (unsafe for the bike's
      // speed/load) and to recommend meeting-or-exceeding it.
      curOeRating = '';
      if (sizes) curOeRating = /front/i.test(activePos) ? (sizes.front_rating || '') : (sizes.rear_rating || '');
      var preferred = null, prefRank = -2;   // for the bike-size best-rating pick
      var pageOpt = null, bestOpt = null, bestRank = -2;
      skus.filter(function (s) { return s.position === activePos; })
          .forEach(function (s) {
            var opt = document.createElement('option');
            opt.value = s.url;
            opt.setAttribute('data-rating', s.rating || '');   // for the OEM badge
            opt.textContent = sizeRatingLabel(s);              // clean: size + rating only
            sizeSelect.appendChild(opt);
            var rank = ratingRank(s.rating);
            if (rank > bestRank) { bestRank = rank; bestOpt = opt; }
            if (normalizeUrl(s.url) === pageUrl) pageOpt = opt;
            // Prefer the bike's size; among those, the best rating wins (so the
            // default meets-or-exceeds OE whenever any in-size tire does).
            if (wantCore && sizeCore(s.size) === wantCore && rank > prefRank) {
              prefRank = rank; preferred = opt;
            }
          });
      var pick = preferred || pageOpt || bestOpt;
      if (pick) pick.selected = true;
      renderOeBadge();
      // After the size list is rebuilt, sync the page price + spec-chart part
      // number to whatever is now selected (the bike's size when known, else
      // the page's own SKU).
      if (sizeSelect.value) { updatePagePrice(sizeSelect.value); updateSpecRows(sizeSelect.value); }
    }

    sizeSelect.addEventListener('change', function () {
      renderOeBadge();
      if (sizeSelect.value) { updatePagePrice(sizeSelect.value); updateSpecRows(sizeSelect.value); }
    });

    positions.forEach(function (p) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gtp-pos-btn' + (p === current.position ? ' is-active' : '');
      btn.textContent = p;
      btn.setAttribute('aria-pressed', p === current.position ? 'true' : 'false');
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        posDiv.querySelectorAll('.gtp-pos-btn').forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        updateSizes(p);
      });
      posDiv.appendChild(btn);
    });

    if (positions.length >= 2) {
      posRow.appendChild(posLabel);
      posRow.appendChild(posDiv);
      box.appendChild(posRow);
    } else if (positions.length === 1) {
      // Single-position family (only fronts OR only rears are stocked for this
      // model) — there is no front/rear toggle to show, so say so plainly instead
      // of leaving a bare size dropdown that reads as "missing selector".
      var onlyTag = document.createElement('div');
      onlyTag.className = 'gtp-onlytag';
      onlyTag.textContent = 'Only ' + positions[0].toLowerCase() + ' options available';
      onlyTag.style.cssText = 'font-family:"Oswald",sans-serif;font-size:10.5px;letter-spacing:2px;'
        + 'text-transform:uppercase;color:#c9a84c;background:#15130d;border:1px solid #2a2a2e;'
        + 'border-left:3px solid #c9a84c;padding:8px 12px;margin:0 0 2px;';
      box.appendChild(onlyTag);
    }

    sizeRow.appendChild(sizeLabel);
    // If there's only one size for the current position AND no other position
    // to switch to, replace the dropdown with a static readout — a 1-option
    // <select> looks broken.
    var onlyOnePosition = positions.length < 2;
    var sizesForCurrent = skus.filter(function (s) { return s.position === (current.position || positions[0]); });
    if (onlyOnePosition && sizesForCurrent.length === 1) {
      var staticSize = document.createElement('div');
      staticSize.className = 'gtp-size-static';
      staticSize.textContent = sizeRatingLabel(sizesForCurrent[0]) +
        (current.position ? '  ·  ' + current.position : '');
      sizeRow.appendChild(staticSize);
      // Keep the (hidden) select around so the Add to Cart handler still
      // has a sizeSelect.value to read.
      sizeSelect.style.display = 'none';
      selectWrap.style.display = 'none';
      sizeRow.appendChild(selectWrap);
    } else {
      sizeRow.appendChild(selectWrap);
    }
    box.appendChild(sizeRow);
    box.appendChild(oeBadge);
    box.appendChild(oosBadge);

    // Add-to-cart button — fetches the chosen SKU's first variant and POSTs
    // to /cart/add.js, then opens the cart drawer. No navigation to another
    // PDP required.
    var goBtn = document.createElement('button');
    goBtn.type = 'button';
    goBtn.className = 'gtp-go';
    var goLabel = document.createElement('span');
    goLabel.textContent = 'Add to cart';
    var goArrow = document.createElement('span');
    goArrow.className = 'gtp-arrow';
    goArrow.textContent = '→';
    goBtn.appendChild(goLabel);
    goBtn.appendChild(goArrow);

    function setLabel(text, disabled) {
      goLabel.textContent = text;
      goBtn.disabled = !!disabled;
      goBtn.style.opacity = disabled ? '.7' : '';
      goBtn.style.cursor = disabled ? 'wait' : 'pointer';
    }

    goBtn.addEventListener('click', function () {
      var pickedUrl = sizeSelect.value;
      if (!pickedUrl) return;
      var handle = (pickedUrl.match(/\/products\/([^/?#]+)/) || [])[1];
      if (!handle) return;
      setLabel('Adding...', true);
      fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('product fetch ' + r.status); return r.json(); })
        .then(function (prod) {
          // Each tire SKU is its own Shopify product with exactly ONE variant
          // (verified: 0 of 521 tire products have more than one variant).
          // The size+position the user picked maps 1:1 to this product, and
          // this product's only variant IS that exact size. We never pick
          // across products — `handle` came straight from sizeSelect.value.
          var variants = prod.variants || [];
          if (variants.length === 0) throw new Error('no variants');
          if (variants.length > 1) {
            // Safety net: if a tire is ever set up with multiple variants
            // (pack size, compound), refuse to guess rather than silently
            // pick one. This won't fire today.
            throw new Error('product has multiple variants — picker needs an update');
          }
          var variant = variants[0];
          if (!variant.available) throw new Error('out of stock');
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ id: variant.id, quantity: 1 })
          });
        })
        .then(function (r) { if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || ('cart add ' + r.status)); }); return r.json(); })
        .then(function () {
          setLabel('Added ✓', false);
          // Pop the cart drawer — theme listens to multiple event names.
          document.dispatchEvent(new CustomEvent('cart:open'));
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
          document.documentElement.classList.add('js-drawer-open');
          var drawer = document.getElementById('CartDrawer');
          if (drawer) drawer.classList.add('drawer--is-open');
          setTimeout(function () { setLabel('Add to cart', false); }, 1800);
        })
        .catch(function (err) {
          console.warn('[gara-tire] add to cart failed', err);
          setLabel(/sold out|inventory|stock/i.test(String(err)) ? 'Out of stock' : 'Try again', false);
          setTimeout(function () { setLabel('Add to cart', false); }, 2200);
        });
    });
    box.appendChild(goBtn);

    target.parentNode.insertBefore(box, target);
    updateSizes(current.position || positions[0]);
  }

  // Resolve the current page's tire family (if we're on a tire PDP) so that
  // "You may also like" can be filtered to OTHER families. Returns '' when
  // we're not on a tire PDP or the URL isn't in the index.
  function currentPageFamily(urlIndex) {
    if (!/\/products\//.test(window.location.pathname)) return '';
    var u = normalizeUrl(window.location.pathname);
    return urlIndex[u] || urlIndex[normalizeUrl(u)] || '';
  }

  // Re-group recommendations after Shopify's async fetch replaces the
  // placeholder. Hides every card in the same family as the current PDP,
  // and dedupes the remaining families to one card each (so the strip
  // shows Diablo Rosso IV, Supercorsa V4, Angel GT II, etc -- not three
  // sizes of the same family).
  function watchRecommendations(families, urlIndex, currentFamily) {
    var nodes = document.querySelectorAll('product-recommendations, .product-recommendations, [data-section-type="product-recommendations"]');
    if (!nodes.length) return;
    function regroupAndCleanup(host) {
      groupCards(host, families, urlIndex, currentFamily);
      // If every card got hidden by same-family filtering, hide the whole
      // section so we don't show an empty "You may also like" header.
      var visibleCards = host.querySelectorAll('a[data-tire-family]:not([style*="display: none"]):not([data-tire-hidden]):not([data-tire-hidden-same-family])');
      var totalCards   = host.querySelectorAll('a[data-tire-family]');
      if (totalCards.length > 0 && visibleCards.length === 0) {
        var section = host.closest('.shopify-section') || host;
        section.style.display = 'none';
      }
    }
    nodes.forEach(function (host) {
      // Run once now in case recs are already in the DOM.
      regroupAndCleanup(host);
      // And watch for the async injection.
      var obs = new MutationObserver(function (mutations) {
        // Only re-run if new product anchors landed.
        var sawNewCards = mutations.some(function (m) {
          return Array.from(m.addedNodes).some(function (n) {
            return n.nodeType === 1 && (n.matches && n.matches('a[href*="/products/"]') || (n.querySelector && n.querySelector('a[href*="/products/"]')));
          });
        });
        if (sawNewCards) {
          // Defer one frame so all sibling cards have landed.
          requestAnimationFrame(function () { regroupAndCleanup(host); });
        }
      });
      obs.observe(host, { childList: true, subtree: true });
    });
  }

  // MX bucket tags (the tubes nudge below keys off these — MX knobbies are
  // tube-type, so a cart holding one and no tube gets the Shop Tubes panel).
  var MX_BUCKET_TAGS = ['tires-mx-soft', 'tires-mx-intermediate', 'tires-mx-hard'];

  // ── CART: "complete the set" pair suggester ─────────────────────────────
  // families.json-driven, replacing the old Liquid implementation that scanned
  // collections['moto-tires'].products — Liquid caps that loop at 50 products,
  // so for most of the 520-tire catalog the server never found the family-mate
  // and rendered NOTHING (the bug Benjamin saw 2026-06-12). The drawer snippet
  // now ships only the styled shell; this module fills it from /cart.js + the
  // same families/urlIndex the grouper already loads. DOM contract preserved:
  // [data-pair-family]/[data-pair-which] panels, .gara-pair-cart-item cards,
  // data-pair-add / data-pair-dismiss buttons, .has-pair-suggest zone gating,
  // sessionStorage gara_pair_dismiss_fam_<family>, window.garaPairSuggestInit.
  function initCartPair(families, urlIndex) {
    function zoneEl() { return document.querySelector('#CartDrawer [data-gara-pair-cart]'); }

    function updateZoneState() {
      var anyVisible = false;
      document.querySelectorAll('#CartDrawer .gara-pair-cart-item').forEach(function (it) {
        if (it.style.display !== 'none') anyVisible = true;
      });
      document.querySelectorAll('.gara-cart-suggest-zone').forEach(function (z) {
        if (anyVisible) z.classList.add('has-pair-suggest');
        else z.classList.remove('has-pair-suggest');
      });
      document.dispatchEvent(new CustomEvent('gara-pair-suggest:change', { detail: { visible: anyVisible } }));
    }

    function dismissed(fam) {
      try { return sessionStorage.getItem('gara_pair_dismiss_fam_' + fam) === '1'; } catch (_) { return false; }
    }

    // ── "Don't forget your tubes" panel (Benjamin 2026-07-16) ─────────────
    // Same treatment as the pair suggester: shell lives in
    // gara-cart-tire-pair-suggest.liquid ([data-gara-tube-cart]); shown while
    // the cart holds an MX-bucket tire and NO inner tube. Runs on every
    // rebuild() so it tracks cart changes (add tube -> panel goes away).
    // Bucket comes from each cart tire's product.js tags (cached per handle).
    var tubeTagCache = {};
    function syncTubeBlock(cart, urlIdx) {
      var block = document.querySelector('#CartDrawer [data-gara-tube-cart]');
      if (!block) return;
      function hide() { block.style.display = 'none'; }
      try { if (sessionStorage.getItem('gara_tube_dismiss') === '1') return hide(); } catch (_) {}
      var items = (cart && cart.items) || [];
      var hasTube = items.some(function (it) {
        return /\btube\b/i.test(it.product_title || it.title || '');
      });
      if (hasTube) return hide();
      var tireHandles = [];
      items.forEach(function (it) {
        if (it.handle && urlIdx['/products/' + it.handle] && tireHandles.indexOf(it.handle) === -1) {
          tireHandles.push(it.handle);
        }
      });
      if (!tireHandles.length) return hide();
      Promise.all(tireHandles.map(function (h) {
        if (tubeTagCache[h]) return Promise.resolve(tubeTagCache[h]);
        return fetch('/products/' + h + '.js', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (p) { return (tubeTagCache[h] = (p && p.tags) || []); })
          .catch(function () { return []; });
      })).then(function (tagSets) {
        var anyMx = tagSets.some(function (tags) {
          return tags.some(function (t) { return MX_BUCKET_TAGS.indexOf(t) !== -1; });
        });
        block.style.display = anyMx ? '' : 'none';
      });
    }
    if (!window.__garaTubeDismissBound) {
      window.__garaTubeDismissBound = true;
      // Delegated: the drawer zone re-renders on cart changes, so a direct
      // listener on the shell button would be lost with each swap.
      document.addEventListener('click', function (e) {
        var d = e.target.closest && e.target.closest('[data-tube-dismiss]');
        if (!d) return;
        try { sessionStorage.setItem('gara_tube_dismiss', '1'); } catch (_) {}
        var b = document.querySelector('#CartDrawer [data-gara-tube-cart]');
        if (b) b.style.display = 'none';
      });
    }

    // One suggestion card. `cands` = the family's opposite-position rows;
    // `exact` = a single bike-size-matched row (render no size choice), else
    // render a size <select> so the rider picks (never guess a size — #1006).
    function renderPanel(panels, job) {
      var panel = document.createElement('div');
      panel.className = 'gara-pair-cart-options';
      panel.setAttribute('data-pair-family', job.fam);
      panel.setAttribute('data-pair-which', job.which);

      var item = document.createElement('div');
      item.className = 'gara-pair-cart-item';
      item.setAttribute('data-size', job.exact ? job.pick.size : (job.cands[0] && job.cands[0].size) || '');

      var dis = document.createElement('button');
      dis.type = 'button';
      dis.className = 'gara-pair-cart-dismiss';
      dis.setAttribute('aria-label', 'Dismiss suggestion');
      dis.innerHTML = '&times;';
      dis.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        try { sessionStorage.setItem('gara_pair_dismiss_fam_' + job.fam, '1'); } catch (_) {}
        panel.style.display = 'none';
        var stillAny = false;
        document.querySelectorAll('#CartDrawer [data-pair-family]').forEach(function (p) {
          if (p.style.display !== 'none') stillAny = true;
        });
        var z = zoneEl();
        if (!stillAny && z) z.style.display = 'none';
        updateZoneState();
      });
      item.appendChild(dis);

      var link = document.createElement('a');
      link.className = 'gara-pair-cart-link';
      var img = document.createElement('div');
      img.className = 'gara-pair-cart-img';
      var meta = document.createElement('div');
      meta.className = 'gara-pair-cart-meta';
      var cat = document.createElement('div');
      cat.className = 'gara-pair-cart-cat';
      var name = document.createElement('div');
      name.className = 'gara-pair-cart-name';
      name.textContent = job.fam;
      var price = document.createElement('div');
      price.className = 'gara-pair-cart-price';
      meta.appendChild(cat); meta.appendChild(name); meta.appendChild(price);
      link.appendChild(img); link.appendChild(meta);
      item.appendChild(link);

      var sel = null;
      if (job.exact) {
        cat.textContent = 'Complete the set — ' + job.which + ' (' + job.pick.size + ') · matches your bike';
      } else {
        cat.textContent = 'Complete the set — pick your ' + job.which + ' size';
        sel = document.createElement('select');
        sel.className = 'gara-pair-cart-sizesel';
        sel.setAttribute('aria-label', 'Choose ' + job.which + ' tire size');
        job.cands.forEach(function (c) {
          var o = document.createElement('option');
          o.value = c.url; o.textContent = sizeRatingLabel(c);
          sel.appendChild(o);
        });
        meta.appendChild(sel);
      }

      var add = document.createElement('button');
      add.type = 'button';
      add.className = 'gara-pair-cart-add';
      add.setAttribute('data-pair-add', '');
      add.textContent = 'Add';
      item.appendChild(add);

      function chosen() { return job.exact ? job.pick : job.cands.filter(function (c) { return c.url === sel.value; })[0]; }

      // Hydrate image/price/availability + the card link from the chosen SKU's
      // own product (families rows carry url/size/price only).
      function hydrate() {
        var c = chosen();
        if (!c) return;
        link.href = c.url;
        item.setAttribute('data-size', c.size);
        price.textContent = moneyFromCents(c.price);
        var h = (c.url.match(/\/products\/([^/?#]+)/) || [])[1];
        if (!h) return;
        fetch('/products/' + h + '.js', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (prod) {
            if (!prod) return;
            if (prod.featured_image) {
              var src = prod.featured_image +
                (prod.featured_image.indexOf('?') === -1 ? '?width=160' : '&width=160');
              img.innerHTML = '<img src="' + src + '" alt="" loading="lazy">';
            }
            var v = prod.variants && prod.variants[0];
            if (v) {
              price.textContent = moneyFromCents(v.price);
              if (!v.available) { add.disabled = true; add.textContent = 'Sold out'; }
              else { add.disabled = false; if (add.textContent === 'Sold out') add.textContent = 'Add'; }
              add.setAttribute('data-variant-id', String(v.id));
            }
          }).catch(function () {});
      }
      if (sel) sel.addEventListener('change', hydrate);
      hydrate();
      panel.appendChild(item);

      add.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        if (add.disabled || add.classList.contains('is-loading')) return;
        var c = chosen();
        if (!c) return;
        var h = (c.url.match(/\/products\/([^/?#]+)/) || [])[1];
        if (!h) return;
        add.classList.add('is-loading'); add.textContent = '...';
        fetch('/products/' + h + '.js', { credentials: 'same-origin' })
          .then(function (r) { if (!r.ok) throw new Error('product ' + r.status); return r.json(); })
          .then(function (prod) {
            var v = prod.variants && prod.variants[0];
            if (!v) throw new Error('no variant');
            if (!v.available) throw new Error('sold out');
            return fetch('/cart/add.js', {
              method: 'POST', credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: v.id, quantity: 1 })
            });
          })
          .then(function (r) { if (!r.ok) throw new Error('cart add ' + r.status); return r.json(); })
          .then(function () {
            add.classList.remove('is-loading'); add.classList.add('is-done'); add.textContent = 'Added';
            document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
          })
          .catch(function (err) {
            add.classList.remove('is-loading');
            add.textContent = /sold out/i.test(String(err)) ? 'Sold out' : 'Try again';
          });
      });

      panels.appendChild(panel);
    }

    function rebuild() {
      var zone = zoneEl();
      if (!zone) return;
      var panels = zone.querySelector('[data-pair-panels]');
      if (!panels) return;
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (!cart) return;
          syncTubeBlock(cart, urlIndex);
          var inCart = {};   // family -> {front:bool, rear:bool}
          (cart.items || []).forEach(function (it) {
            var url = '/products/' + it.handle;
            var fam = urlIndex[url];
            if (!fam) return;
            var row = (families[fam] || []).filter(function (s) { return normalizeUrl(s.url) === url; })[0];
            var pos = ((row && row.position) || '').toLowerCase();
            if (pos !== 'front' && pos !== 'rear') return;
            (inCart[fam] = inCart[fam] || {})[pos] = true;
          });
          var sizes = ymmSizes();
          var jobs = [];
          Object.keys(inCart).forEach(function (fam) {
            if (dismissed(fam)) return;
            var have = inCart[fam];
            [['front', 'rear'], ['rear', 'front']].forEach(function (pair) {
              var has = pair[0], missing = pair[1];
              if (!have[has] || have[missing]) return;
              var cands = (families[fam] || []).filter(function (s) {
                return (s.position || '').toLowerCase() === missing;
              });
              if (!cands.length) return;
              var want = '';
              if (sizes) {
                want = missing === 'front'
                  ? (sizeCore(sizes.front) || sizeCore(sizes.front_alt))
                  : (sizeCore(sizes.rear) || sizeCore(sizes.rear_alt));
              }
              // Among the bike-size matches, default to the BEST-rated variant
              // (fastest, then highest-load) so the auto-added tire is the best
              // in that size — same rule the picker dropdown uses.
              var pick = null;
              if (want) {
                cands.filter(function (s) { return sizeCore(s.size) === want; })
                     .forEach(function (s) {
                       if (!pick || ratingRank(s.rating) > ratingRank(pick.rating)) pick = s;
                     });
              }
              jobs.push({ fam: fam, which: missing, pick: pick, cands: cands, exact: !!pick });
            });
          });
          panels.innerHTML = '';
          if (!jobs.length) { zone.style.display = 'none'; updateZoneState(); return; }
          jobs.slice(0, 3).forEach(function (j) { renderPanel(panels, j); });
          zone.style.display = '';
          updateZoneState();
        })
        .catch(function () {});
    }

    // The drawer's refreshSuggestZone() calls this after every zone swap;
    // also re-run on bike change so an exact-size pick replaces the chooser.
    window.garaPairSuggestInit = rebuild;
    if (!window.__garaPairYmmBound) {
      window.__garaPairYmmBound = true;
      document.addEventListener('gara-ymm:change', function () {
        if (typeof window.garaPairSuggestInit === 'function') window.garaPairSuggestInit();
      });
    }
    rebuild();
  }

  function init() {
    loadFamilies().then(function (families) {
      if (!families || Object.keys(families).length === 0) return;
      var urlIndex = buildUrlIndex(families);
      var currentFamily = currentPageFamily(urlIndex);
      var onPDP = /\/products\//.test(window.location.pathname);
      // Collection grouping (and its bike-filter "no complete set" banner) must run
      // ONLY on /collections/ grids — NOT /cart (anchors are cart line items, the
      // banner leaked there) and NOT /search (an explicit query: the bike "complete
      // set" filter wrongly blanked tire searches; search has its own fit handling).
      var onCollection = /\/collections\//.test(window.location.pathname);
      // TIRE collection = the handle segment contains 'tire' (the same rule
      // gara-main-collection.liquid uses to render the tire finder). The
      // set-card injection must NEVER run outside tire collections — it put
      // Dunlop D401 tire cards into the Oil Change Kits grid (2026-08-14).
      var onTireCollection = onCollection &&
        /tire/.test(((window.location.pathname.split('/collections/')[1] || '').split('/')[0] || ''));

      // Collapse the collection to one card per family. groupCards is YMM-aware:
      //   no bike  → master = first card of each family (clean browse)
      //   bike set → master = a card that FITS the bike; families with no
      //              fitting size are hidden. One card per fitting family — no
      //              front/rear duplicates, no wrong-size master that vanishes.
      // ONE collection refresh path so every trigger stays consistent: regroup,
      // sync the "unknown sizes" note, and sync the bike-filter bar (Show-all
      // toggle + the no-complete-set note).
      function refreshGrid(root) {
        var summary = groupCards(root || document, families, urlIndex);
        if (!onPDP) { syncGridUnknote(document); syncBikeFilterUI(summary); }
        if (onTireCollection && summary && summary.bikeActive && !summary.showAll && !summary.anyComplete) {
          ensureSetCards();
        }
        return summary;
      }

      // THE GRID FIX (Benjamin 2026-08-14, third round): stop depending on the
      // 7-page full-HTML lazy loader to get the set families' cards into the
      // DOM — on a weak mobile connection it stalls and the rider stares at
      // partials forever. We KNOW the exact products that pair the bike
      // (dataCompleteSets); fetch just those products' tiny /products/X.js
      // payloads (~3 KB each) and inject REAL native-markup cards at the top
      // of the grid, then regroup — complete-mode then shows them and hides
      // the one-end partials. One fetch wave per bike; already-present SKUs
      // are never duplicated; a failed fetch just leaves that family to the
      // page loader.
      var _setCardsKey = '';
      function ensureSetCards() {
        try {
          var y = ymmSizes();
          if (!y) return;
          var key = (y.front || '') + '|' + (y.rear || '');
          if (_setCardsKey === key) return;
          var sets = dataCompleteSets();
          if (!sets.length) return;
          _setCardsKey = key;
          var grid = document.querySelector('.gara-product-grid') ||
                     (function () { var c = document.querySelector('a[data-tire-family]'); return c && c.parentElement; })();
          if (!grid) return;
          var present = {};
          grid.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
            present[normalizeUrl(a.getAttribute('href'))] = 1;
          });
          var wanted = [];
          sets.slice(0, 6).forEach(function (s) {
            [s.front, s.rear].forEach(function (e) {
              if (e && e.url && !present[normalizeUrl(e.url)]) { present[normalizeUrl(e.url)] = 1; wanted.push(e); }
            });
          });
          if (!wanted.length) return;
          Promise.all(wanted.map(function (e) {
            return fetch(e.url.split('?')[0] + '.js', { credentials: 'same-origin' })
              .then(function (r) { return r.ok ? r.json() : null; })
              .then(function (p) { return p ? { entry: e, product: p } : null; })
              .catch(function () { return null; });
          })).then(function (rows) {
            var frag = document.createDocumentFragment(), appended = 0;
            rows.forEach(function (row) {
              if (!row) return;
              var p = row.product, e = row.entry;
              var sku = (p.variants && p.variants[0] && p.variants[0].sku) || '';
              if (!sku || document.querySelector('.gara-pcard[data-sku="' + sku + '"]')) return;
              var a = document.createElement('a');
              a.className = 'gara-pcard' + (p.available ? '' : ' oos');
              a.href = e.url;
              a.setAttribute('data-sku', sku);
              a.setAttribute('data-gara-setcard', '1');
              a.setAttribute('aria-label', p.title || '');
              var img = p.featured_image
                ? '<div class="gara-pcard-img"><img class="gara-pcard-img-primary" loading="lazy" src="' + escHtml(p.featured_image) + '" alt=""></div>'
                : '<div class="gara-pcard-img"><div class="gara-pcard-noimg">No image</div></div>';
              a.innerHTML = img +
                '<div class="gara-pcard-meta"><div class="gara-pcard-vendor">' + escHtml(p.vendor || '') + '</div>' +
                '<h3 class="gara-pcard-title">' + escHtml(p.title || '') + '</h3>' +
                '<div class="gara-pcard-price-row"><span class="gara-pcard-price">' + moneyFromCents(p.price) + '</span></div></div>';
              frag.appendChild(a);
              appended++;
            });
            if (appended) {
              grid.insertBefore(frag, grid.firstChild);
              ungroupCollection(document);
              refreshGrid(document);
            }
          });
        } catch (_) {}
      }
      function setShowAll(v) {
        showAll = v;
        // "Show all tires" must also lift the tire-FINDER's size filter
        // (.gtf-hidden) — it is a separate hiding layer painted by
        // gara-tire-finder.js applyFilter. Without this, Show-all unhid only
        // the grouper's own cards while gtf-hidden kept ~everything invisible
        // AND family masters were picked from hidden cards, so the "all tires"
        // view rendered a single card (2009 FXDC ad complaint, 2026-08-14).
        if (v) {
          document.querySelectorAll('.gtf-hidden').forEach(function (el) { el.classList.remove('gtf-hidden'); });
        }
        ungroupCollection(document);
        refreshGrid(document);
      }
      // Bar above the grid when a bike is selected: says what's shown (complete
      // sets / partial fits / all) and offers the Show-all toggle.
      //
      // DATA-LEVEL COMPLETE SETS (2026-08-14, the 2009 FXDC ad complaint): the
      // DOM classification above can only see the cards Shopify rendered, and
      // `paginate by 2500` is silently CAPPED at 250/page — on a 1,600-tire
      // collection the pairing families' size cards usually live on pages 2+,
      // so anyComplete read false and the banner told a stock Dyna rider we
      // couldn't outfit his bike while 4 complete sets sat in families.json.
      // dataCompleteSets() answers from the DATA (every tire in the catalog),
      // immune to pagination AND brand-page scoping. It is used ONLY to pick the
      // honest banner WORDING (Benjamin 2026-08-14: no chips, the GRID is the UI)
      // while gara-tire-finder.js's page loader brings the set families' real
      // cards into the DOM; complete-mode then shows exactly those cards.
      function dataCompleteSets() {
        var y = ymmSizes();
        if (!y) return [];
        var fCores = [y.front, y.front_alt].filter(Boolean).map(sizeCore).filter(Boolean);
        var rCores = [y.rear, y.rear_alt].filter(Boolean).map(sizeCore).filter(Boolean);
        if (!fCores.length || !rCores.length) return [];   // a set needs both declared ends
        function endEntry(skus, cores, front) {
          var best = null;
          for (var i = 0; i < skus.length; i++) {
            var e = skus[i];
            var pos = (e.position || '').toLowerCase();
            var posOK = front ? (pos === 'front' || pos === '' || pos === 'front/rear' || pos === 'universal')
                              : (pos === 'rear'  || pos === '' || pos === 'front/rear' || pos === 'universal');
            if (!posOK || cores.indexOf(sizeCore(e.size)) === -1) continue;
            if (!best || (e.price > 0 && (best.price <= 0 || e.price < best.price))) best = e;
          }
          return best;
        }
        var out = [];
        Object.keys(families).forEach(function (fam) {
          var skus = families[fam] || [];
          var f = endEntry(skus, fCores, true);
          var r = endEntry(skus, rCores, false);
          if (f && r) out.push({ family: fam, front: f, rear: r });
        });
        out.sort(function (a, b) { return (a.front.price + a.rear.price) - (b.front.price + b.rear.price); });
        return out;
      }
      function syncBikeFilterUI(summary) {
        var prior = document.querySelector('[data-gara-tirebar]');
        if (prior) prior.remove();
        var firstCard = document.querySelector('a[data-tire-family]');
        var grid = firstCard && (firstCard.closest('ul, ol, .grid, .collection-grid, [data-collection-grid]') || firstCard.parentElement);
        if (!summary || !summary.bikeActive || !grid) return;
        injectUnknoteStyles();
        var bike = escHtml(bikeLabel()), msg;
        // Benjamin 2026-08-14: NO set chips in the banner — the GRID is the UI.
        // dataCompleteSets() (full-catalog answer, pagination-immune) only decides
        // the honest WORDING while the page loader is still bringing the set
        // families' real cards into the DOM; once they land, the grouper's
        // complete-mode shows exactly those cards and hides the one-end partials.
        if (summary.showAll) msg = 'Showing <b>all tires</b>. ';
        else if (summary.anyComplete) msg = 'Showing complete tire sets for your <b>' + bike + '</b>. ';
        else if (dataCompleteSets().length) msg = 'Finding the complete tire sets for your <b>' + bike + '</b>&hellip; ';
        else msg = "We don't have a complete tire set (front + rear) for your <b>" + bike + "</b> yet, so we're showing tires that fit one end. ";
        var nb = document.createElement('div');
        nb.className = 'gara-tire-unknote';
        nb.setAttribute('data-gara-tirebar', '1');
        nb.innerHTML = msg + '<button type="button" class="gara-tire-showall">' +
          (summary.showAll ? 'Show tires for my bike' : 'Show all tires') + '</button>';
        grid.parentNode.insertBefore(nb, grid);
        nb.querySelector('.gara-tire-showall').addEventListener('click', function () { setShowAll(!summary.showAll); });
      }

      if (onCollection) refreshGrid(document);

      renderPdpPicker(families, urlIndex);
      initCartPair(families, urlIndex);   // drawer exists site-wide
      if (currentFamily) watchRecommendations(families, urlIndex, currentFamily);

      // Re-group on section reload (filter/sort/pagination).
      document.addEventListener('shopify:section:load', function (e) {
        if (onPDP) groupCards(e.target, families, urlIndex, currentFamily || undefined);
        else if (onCollection) refreshGrid(e.target);
      });

      // Re-collapse with the new match set whenever the rider selects/clears a
      // bike on the page (no reload), AND whenever the finder appends lazy-loaded
      // pages (gara-grid:refilled) — otherwise those appended matching cards show
      // ungrouped (front+rear duplicates). Reset, then group against current YMM.
      if (onCollection) {
        document.addEventListener('gara-ymm:change', function () { showAll = false; ungroupCollection(document); refreshGrid(document); });
        document.addEventListener('gara-grid:refilled', function () { ungroupCollection(document); refreshGrid(document); });
      }

      // ── Homepage "top tires" carousels: when a bike is selected, show only the
      //    tire FAMILIES that cover the bike as a MATCHING SET — the bike's front
      //    size AND its rear size both available in that family. No bike → show all.
      //    Families with only one position (the fragmented sport tires) can never
      //    be a set, so they drop out when a bike is active. Strip hides if nothing
      //    matches (no empty rail). Match by size+position (families carry no SKU).
      function familyMatchesBike(fam, sizes) {
        var skus = families[fam];
        if (!skus || !skus.length) return false;
        var fCores = [sizes.front, sizes.front_alt].filter(Boolean).map(sizeCore).filter(Boolean);
        var rCores = [sizes.rear, sizes.rear_alt].filter(Boolean).map(sizeCore).filter(Boolean);
        if (!fCores.length && !rCores.length) return true;
        var hasF = !fCores.length || skus.some(function (s) {
          return (s.position === 'Front' || !s.position) && fCores.indexOf(sizeCore(s.size)) !== -1;
        });
        var hasR = !rCores.length || skus.some(function (s) {
          return (s.position === 'Rear' || !s.position) && rCores.indexOf(sizeCore(s.size)) !== -1;
        });
        return hasF && hasR;
      }
      function filterCarousel() {
        var strips = document.querySelectorAll('.gara-tt-strip');
        if (!strips.length) return;
        var sizes = ymmSizes();
        var haveBike = !!(sizes && (sizes.front || sizes.rear));
        strips.forEach(function (strip) {
          var shown = 0;
          strip.querySelectorAll('.gara-tt-tc').forEach(function (card) {
            // The "Shop all tires" end card always rides on the strip's overall
            // visibility (hidden only when the whole strip is empty, below) - never
            // filtered out by bike match.
            if (card.classList.contains('gara-tt-all')) return;
            var show = true;
            if (haveBike) {
              var fam = card.getAttribute('data-tt-family');
              show = fam ? familyMatchesBike(fam, sizes) : false;
            }
            card.style.display = show ? '' : 'none';
            if (show) shown++;
          });
          // Bike active with zero matching sets in this strip -> hide it (no empty
          // rail). Inline display only; the MX data-tt-empty !important rule still wins.
          strip.style.display = (haveBike && shown === 0) ? 'none' : '';
        });
      }
      filterCarousel();
      document.addEventListener('gara-ymm:change', filterCarousel);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
